import { useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { SHOWCASE_SCREEN_SIZE, SHOWCASE_SETTINGS, type ShowcaseDevice } from "./productShowcaseConfig";

export interface ShowcaseScreen {
  id: string;
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
}

interface Layer extends ShowcaseScreen {
  /** +1 slides in from the right, -1 from the left, 0 just appears. */
  direction: number;
  /** pending: loading under the current screen; shown: on screen; leaving: fading out. */
  phase: "pending" | "shown" | "leaving";
}

/**
 * The app screen inside the frame. A new screen loads invisibly on top of the
 * current one; once it has loaded it fades and slides in while the old one
 * fades out underneath and is dropped, so the frame never goes blank, moves
 * or changes size.
 */
function FeatureScreen({ screen, index, device }: { screen: ShowcaseScreen; index: number; device: ShowcaseDevice }) {
  const [layers, setLayers] = useState<Layer[]>([{ ...screen, direction: 0, phase: "shown" }]);
  const lastIndex = useRef(index);
  const latest = useRef(screen);
  latest.current = screen;
  const size = SHOWCASE_SCREEN_SIZE[device];

  useEffect(() => {
    const next = latest.current;
    const direction = index === lastIndex.current ? 0 : index > lastIndex.current ? 1 : -1;
    lastIndex.current = index;
    setLayers((current) => {
      const settled = current.filter((layer) => layer.phase !== "pending");
      if (settled[settled.length - 1]?.src === next.src) return settled;
      return [...settled, { ...next, direction, phase: "pending" }];
    });
  }, [screen.src, index]);

  const reveal = (src: string) => {
    setLayers((current) => {
      const incoming = current.find((layer) => layer.src === src && layer.phase === "pending");
      if (!incoming) return current;
      return current.map((layer) =>
        layer === incoming
          ? { ...layer, phase: "shown" }
          : layer.phase === "shown"
            ? { ...layer, phase: "leaving", direction: incoming.direction }
            : layer,
      );
    });
    window.setTimeout(
      () => setLayers((current) => current.filter((layer) => layer.phase !== "leaving")),
      SHOWCASE_SETTINGS.transitionMs + 50,
    );
  };

  return (
    <div className="ps-screen" style={{ aspectRatio: `${size.width} / ${size.height}` }}>
      {layers.map((layer) => {
        const shown = layer.phase === "shown";
        return (
          <img
            key={layer.src}
            src={layer.src}
            srcSet={layer.srcSet}
            sizes={layer.sizes}
            alt={shown ? layer.alt : ""}
            aria-hidden={!shown || undefined}
            width={size.width}
            height={size.height}
            loading="lazy"
            decoding="async"
            onLoad={layer.phase === "pending" ? () => reveal(layer.src) : undefined}
            ref={(img) => {
              if (img && layer.phase === "pending" && img.complete && img.naturalWidth > 0) reveal(layer.src);
            }}
            className={cn(
              "ps-screen-img",
              layer.phase === "pending" && "is-pending",
              layer.phase === "leaving" && "is-leaving",
              shown && layer.direction !== 0 && "is-entering",
            )}
            style={{ "--ps-dir": layer.direction } as CSSProperties}
            data-testid={shown ? `product-showcase-screen-${layer.id}` : undefined}
          />
        );
      })}
    </div>
  );
}

/** A phone or a browser window around the active screen. */
export function DevicePreview({
  device,
  screen,
  index,
  urlLabel,
  panelId,
  panelLabel,
}: {
  device: ShowcaseDevice;
  screen: ShowcaseScreen;
  index: number;
  urlLabel: string;
  panelId: string;
  panelLabel: string;
}) {
  return (
    <div
      key={device}
      id={panelId}
      role="region"
      aria-label={panelLabel}
      className={cn("ps-device", device === "mobile" ? "ps-phone" : "ps-browser")}
      data-testid={`product-showcase-device-${device}`}
    >
      {device === "mobile" ? (
        <div className="ps-phone-body">
          <div className="ps-phone-status" aria-hidden="true">
            <span>9:41</span>
            <span className="ps-phone-island" />
            <span className="ps-phone-battery" />
          </div>
          <FeatureScreen screen={screen} index={index} device={device} />
        </div>
      ) : (
        <div className="ps-browser-body">
          <div className="ps-browser-bar" aria-hidden="true">
            <span className="ps-dot bg-destructive/60" />
            <span className="ps-dot bg-amber-400" />
            <span className="ps-dot bg-success/60" />
            <span className="ps-browser-url">{urlLabel}</span>
          </div>
          <FeatureScreen screen={screen} index={index} device={device} />
        </div>
      )}
    </div>
  );
}
